import { Capacitor } from "@capacitor/core";
import { useConvex } from "convex/react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { mutationRef } from "@/lib/convex/refs";
import { useDeviceId } from "@/lib/device/DeviceProvider";
import {
  getOnboardingTotalSteps,
  stepToScreenId,
  type OnboardingScreenId,
} from "./screenIds";

export type OnboardingPlatform = "ios" | "android" | "web";

export type OnboardingEventType =
  | "session_start"
  | "screen_view"
  | "dwell_time"
  | "cta_click"
  | "paywall_action"
  | "permission_outcome"
  | "onboarding_complete"
  | "onboarding_exit";

export type OnboardingAnalyticsEvent = {
  onboardingSessionId: string;
  eventType: OnboardingEventType;
  step?: number;
  screenId?: OnboardingScreenId | "paywall" | "trial_reassurance" | "notifications";
  timestamp: number;
  dwellMs?: number;
  ctaKind?: string;
  paywallAction?: string;
  paywallPlan?: "yearly" | "lifetime";
  revenueStatus?: "pending_trial" | "realized";
  price?: number;
  currencyCode?: string;
  priceString?: string;
  productIdentifier?: string;
  trialEndsAtMs?: number;
  intlLocale?: string;
  timezone?: string;
  permissionKind?: string;
  permissionOutcome?: string;
  exitReason?: string;
  metadata?: Record<string, string>;
};

type TrackEventsArgs = {
  anonymousDeviceId: string;
  platform: OnboardingPlatform;
  appVersion?: string;
  events: OnboardingAnalyticsEvent[];
};

const trackEventsRef = mutationRef<TrackEventsArgs, { inserted: number }>(
  "onboardingAnalytics:trackEvents",
);

const FLUSH_INTERVAL_MS = 2_000;

function createSessionId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `onb_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolvePlatform(): OnboardingPlatform {
  const platform = Capacitor.getPlatform();
  if (platform === "ios" || platform === "android") return platform;
  return "web";
}

function includeOverlayOnboardingStep(): boolean {
  return Capacitor.getPlatform() === "android";
}

function screenIdForStep(step: number): OnboardingScreenId {
  return stepToScreenId(step, includeOverlayOnboardingStep());
}

function resolveAppVersion(): string | undefined {
  return import.meta.env.VITE_APP_VERSION?.trim() || undefined;
}

export type PaywallActionOptions = {
  metadata?: Record<string, string>;
  paywallPlan?: "yearly" | "lifetime";
  revenueStatus?: "pending_trial" | "realized";
  price?: number;
  currencyCode?: string;
  priceString?: string;
  productIdentifier?: string;
  trialEndsAtMs?: number;
  intlLocale?: string;
  timezone?: string;
};

export function useOnboardingAnalytics() {
  const convex = useConvex();
  const deviceId = useDeviceId();
  const sessionIdRef = useRef(createSessionId());
  const platform = useMemo(() => resolvePlatform(), []);
  const appVersion = useMemo(() => resolveAppVersion(), []);
  const queueRef = useRef<OnboardingAnalyticsEvent[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepEnteredAtRef = useRef<number>(Date.now());
  const currentStepRef = useRef<number>(1);
  const lastViewedStepRef = useRef<number | null>(null);
  const startedRef = useRef(false);

  const flush = useCallback(async () => {
    if (!deviceId || queueRef.current.length === 0) return;
    const batch = queueRef.current.splice(0, queueRef.current.length);
    try {
      await convex.mutation(trackEventsRef, {
        anonymousDeviceId: deviceId,
        platform,
        appVersion,
        events: batch,
      });
    } catch (error) {
      console.warn("Onboarding analytics flush failed", error);
      queueRef.current.unshift(...batch);
    }
  }, [appVersion, convex, deviceId, platform]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      void flush();
    }, FLUSH_INTERVAL_MS);
  }, [flush]);

  const enqueue = useCallback(
    (event: Omit<OnboardingAnalyticsEvent, "onboardingSessionId" | "timestamp"> & { timestamp?: number }) => {
      queueRef.current.push({
        onboardingSessionId: sessionIdRef.current,
        timestamp: event.timestamp ?? Date.now(),
        eventType: event.eventType,
        step: event.step,
        screenId: event.screenId,
        dwellMs: event.dwellMs,
        ctaKind: event.ctaKind,
        paywallAction: event.paywallAction,
        paywallPlan: event.paywallPlan,
        revenueStatus: event.revenueStatus,
        price: event.price,
        currencyCode: event.currencyCode,
        priceString: event.priceString,
        productIdentifier: event.productIdentifier,
        trialEndsAtMs: event.trialEndsAtMs,
        intlLocale: event.intlLocale,
        timezone: event.timezone,
        permissionKind: event.permissionKind,
        permissionOutcome: event.permissionOutcome,
        exitReason: event.exitReason,
        metadata: event.metadata,
      });
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const trackSessionStart = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    enqueue({ eventType: "session_start" });
  }, [enqueue]);

  const trackScreenView = useCallback(
    (step: number) => {
      if (lastViewedStepRef.current === step) return;

      const previousStep = currentStepRef.current;
      if (previousStep !== step) {
        const dwellMs = Date.now() - stepEnteredAtRef.current;
        if (startedRef.current && lastViewedStepRef.current != null && dwellMs > 0) {
          enqueue({
            eventType: "dwell_time",
            step: previousStep,
            screenId: screenIdForStep(previousStep),
            dwellMs,
          });
        }
        currentStepRef.current = step;
        stepEnteredAtRef.current = Date.now();
      }

      lastViewedStepRef.current = step;
      enqueue({
        eventType: "screen_view",
        step,
        screenId: screenIdForStep(step),
      });
    },
    [enqueue],
  );

  const trackCtaClick = useCallback(
    (step: number, ctaKind: "primary" | "secondary" | "skip") => {
      enqueue({
        eventType: "cta_click",
        step,
        screenId: screenIdForStep(step),
        ctaKind,
      });
    },
    [enqueue],
  );

  const trackPaywallAction = useCallback(
    (paywallAction: string, options?: PaywallActionOptions) => {
      enqueue({
        eventType: "paywall_action",
        step: options?.metadata?.step ? Number(options.metadata.step) : undefined,
        screenId: "paywall",
        paywallAction,
        metadata: options?.metadata,
        paywallPlan: options?.paywallPlan,
        revenueStatus: options?.revenueStatus,
        price: options?.price,
        currencyCode: options?.currencyCode,
        priceString: options?.priceString,
        productIdentifier: options?.productIdentifier,
        trialEndsAtMs: options?.trialEndsAtMs,
        intlLocale: options?.intlLocale,
        timezone: options?.timezone,
      });
    },
    [enqueue],
  );

  const trackPermissionOutcome = useCallback(
    (
      step: number,
      permissionKind: "notifications" | "screen_time" | "vpn" | "overlay",
      permissionOutcome: string,
      metadata?: Record<string, string>,
    ) => {
      enqueue({
        eventType: "permission_outcome",
        step,
        screenId: screenIdForStep(step),
        permissionKind,
        permissionOutcome,
        metadata,
      });
    },
    [enqueue],
  );

  const trackOnboardingComplete = useCallback(() => {
    const includeOverlay = includeOverlayOnboardingStep();
    const totalSteps = getOnboardingTotalSteps(includeOverlay);
    enqueue({
      eventType: "onboarding_complete",
      step: totalSteps,
      screenId: "vpn_setup",
    });
    void flush();
  }, [enqueue, flush]);

  const trackOnboardingExit = useCallback(
    (exitReason: string, step: number) => {
      enqueue({
        eventType: "onboarding_exit",
        step,
        screenId: screenIdForStep(step),
        exitReason,
      });
      void flush();
    },
    [enqueue, flush],
  );

  useEffect(() => {
    if (!deviceId) return;
    trackSessionStart();
  }, [deviceId, trackSessionStart]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        void flush();
      }
    };
    const onPageHide = () => {
      void flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      void flush();
    };
  }, [flush]);

  return {
    sessionId: sessionIdRef.current,
    trackScreenView,
    trackCtaClick,
    trackPaywallAction,
    trackPermissionOutcome,
    trackOnboardingComplete,
    trackOnboardingExit,
    flush,
  };
}

export type OnboardingAnalyticsApi = ReturnType<typeof useOnboardingAnalytics>;
